// Input component with field-filled state for required field styling

import { forwardRef, type InputHTMLAttributes } from 'react'
import clsx from 'clsx'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
}

// Check if a value is considered "filled" (non-empty string)
const isFilled = (value: unknown): boolean => {
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return true
  return !!value
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className, required, id, ...props }, ref) => {
    const inputId = id || props.name
    const filled = isFilled(props.value)

    return (
      <div className="form-group">
        {label && (
          <label htmlFor={inputId} className="form-label">
            <span>
              {label}
              {required && <span className={clsx('required', { filled })}>*</span>}
            </span>
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={clsx(
            'form-input',
            {
              'form-input--error': error,
              'field-filled': filled,
            },
            className
          )}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        {error && (
          <span id={`${inputId}-error`} className="form-error">
            {error}
          </span>
        )}
        {helperText && !error && (
          <span className="form-helper">{helperText}</span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
